import { Input } from "@headlessui/react";
import { type FC, type RefObject, useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";
import { Checkbox } from "./Checkbox";

type Option = {
	label: string;
	value: string;
};

export type SearchableMultiselectProps = {
	options: Option[];
	selectedValues: string[];
	onOptionSelect: (selectedValues: string) => void;
	noOptionsMessage?: string;
	searchPlaceholder?: string;
	embedded?: boolean;
};

export const SearchableMultiselect: FC<SearchableMultiselectProps> = ({
	options,
	selectedValues,
	onOptionSelect,
	noOptionsMessage,
	searchPlaceholder,
	embedded,
}) => {
	return (
		<div className="relative" data-testid="searchable-dropdown-multiselect">
			<div className="absolute top-0 z-50 mt-1">
				<div
					className={cn(
						"border border-gray-200 rounded-[4px] shadow-dropdown-shadow",
						"bg-white inline-block w-full min-w-[280px]",
					)}
				>
					<SearchableMultiselectEmbedded
						options={options}
						selectedValues={selectedValues}
						onOptionSelect={onOptionSelect}
						noOptionsMessage={noOptionsMessage}
						searchPlaceholder={searchPlaceholder}
						embedded={embedded}
					/>
				</div>
			</div>
		</div>
	);
};

const SearchableMultiselectSearchInput = ({
	searchPlaceholder = "Search...",
	handleSearch,
}: {
	searchPlaceholder?: string;
	handleSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
	return (
		<Input
			placeholder={searchPlaceholder}
			onChange={handleSearch}
			size={1}
			data-testid="searchable-dropdown-search-input"
			className={cn(
				"w-full px-[10px] py-2 border-t-0 border-l-0 border-b border-gray-200",
				"focus:outline-none text-[13px] text-gray-700",
				"placeholder:text-gray-400 rounded-[4px] min-w-0",
			)}
		/>
	);
};

const SearchableMultiselectOptionsList = ({
	filteredOptions,
	onOptionSelect,
	selectedValues,
	noOptionsMessage = "No options available",
	scrollContainerRef,
}: {
	filteredOptions: Option[];
	onOptionSelect: (value: string) => void;
	selectedValues: string[];
	noOptionsMessage?: string;
	scrollContainerRef?: RefObject<HTMLDivElement | null>;
}) => {
	return (
		<div
			ref={scrollContainerRef}
			data-testid="options-list"
			className={cn("overflow-y-auto scrollbar-med max-h-[176px]", "w-full min-w-48")}
		>
			<div className="w-full">
				<div className="flex flex-col w-full">
					{filteredOptions.length > 0 ? (
						filteredOptions.map((option) => (
							<div
								key={option.value}
								data-testid="dropdown-option"
								className={cn(
									"group cursor-pointer w-full",
									"hover:font-weight-[600]",
									"leading-[21px] hover:bg-gray-100 hover:text-primary",
									"flex items-center py-2 px-[23px]",
								)}
								onClick={() => onOptionSelect(option.value)}
							>
								<div className="inline-flex items-center space-x-[8px] text-gray-700 group-hover:font-medium">
									<Checkbox
										checked={selectedValues.includes(option.value)}
										className="h-[16px] w-[16px] min-h-[16px] min-w-[16px] outline-gray-300 group-hover:outline-[1px] group-hover:outline-gray-500"
									/>
									<span className="text-[13px]">{option.label}</span>
								</div>
							</div>
						))
					) : (
						<div
							data-testid="no-options-message"
							className=" whitespace-nowrap p-4 text-center text-gray-400 text-[13px]"
						>
							{noOptionsMessage}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

// Embeddable version to allow for use outside the default popover
export const SearchableMultiselectEmbedded: FC<SearchableMultiselectProps> = ({
	options,
	selectedValues,
	onOptionSelect,
	noOptionsMessage,
	searchPlaceholder,
	embedded = false,
}) => {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const scrollPositionRef = useRef<number>(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);

	// Sort and filter options whenever dependencies change
	useEffect(() => {
		// Save current scroll position before re-sorting
		if (scrollContainerRef.current) {
			scrollPositionRef.current = scrollContainerRef.current.scrollTop;
		}

		// Apply search filter
		const filtered = searchTerm
			? options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
			: options;

		// Sort: selected items alphabetically first, then unselected items alphabetically
		// Create a new array to avoid mutating the original
		const sorted = [...filtered].sort((a, b) => {
			const aSelected = selectedValues.includes(a.value);
			const bSelected = selectedValues.includes(b.value);

			// If both selected or both unselected, sort alphabetically
			if (aSelected === bSelected) {
				return a.label.localeCompare(b.label);
			}

			// Selected items come first
			return aSelected ? -1 : 1;
		});

		setFilteredOptions(sorted);
	}, [options, selectedValues, searchTerm]);

	// Restore scroll position after filtered options update
	useEffect(() => {
		if (scrollContainerRef.current && scrollPositionRef.current > 0) {
			scrollContainerRef.current.scrollTop = scrollPositionRef.current;
		}
	}, [filteredOptions]);

	const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};

	return (
		<div className="flex flex-col w-full">
			{embedded ? (
				<div className="flex-shrink-0 w-full px-3 py-4 border-b border-gray-200">
					<Input
						placeholder={searchPlaceholder}
						onChange={handleSearch}
						size={1}
						data-testid="searchable-dropdown-search-input"
						className={cn(
							"w-full px-2 py-3 border border-gray-200",
							"focus:outline-none focus:border-gray-500 text-[13px] text-gray-700",
							"placeholder:text-secondary rounded-[6px] min-w-0",
							"leading-[12px] h-9 hover:border-gray-400",
						)}
					/>
				</div>
			) : (
				<div className="flex-shrink-0 w-full">
					<SearchableMultiselectSearchInput
						handleSearch={handleSearch}
						searchPlaceholder={searchPlaceholder}
					/>
				</div>
			)}
			<SearchableMultiselectOptionsList
				scrollContainerRef={scrollContainerRef}
				filteredOptions={filteredOptions}
				onOptionSelect={onOptionSelect}
				selectedValues={selectedValues}
				noOptionsMessage={noOptionsMessage}
			/>
		</div>
	);
};
